import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, MessageTypeEnum, ReplyToMessage } from '../../types/chatMessage';
import { AttachmentIcon, Attachment } from '../attachment/Attachment';
import { Media } from '../../types/media';
import Temp from '../../assets/profile.svg';
import ReadIcon from '../../assets/read.svg';
import DeliveredIcon from '../../assets/delivered.svg';
import ErrorIcon from '../../assets/icons8-error-24.png';
import { User } from '../../types/users';
import { useMutation } from "@tanstack/react-query";
import { readMessage } from "../../services/chatMessages";
import { useUserContext } from "../../context/user";
import Cross from "../../assets/cross.svg";
import { getMediaTypeName } from "../../helpers/media";
import { useChatContext } from "../../context/chats";

interface Props {
    msg: ChatMessage,
    sender: User | undefined
    getFile?: (id: string) => Media | undefined;
}

type MessageStatus = 'SENT' | 'READ' | 'ERROR'

interface ReplyToProps {
    messageToReply: ReplyToMessage,
    showCancelButton: boolean
    onCancelReply: () => void,
}

export const ChatReplyToMessage = ({ messageToReply, onCancelReply, showCancelButton }: ReplyToProps) => {
    const [media, setMedia] = useState<Media | null>(null)

    useEffect(() => {
        const found = messageToReply.media?.length ? messageToReply.media[0] : null;
        setMedia(found)
    }, [messageToReply.media]);


    return <div
        className="w-full h-full border-l-melon-blue border-l-4 flex flex-row flex-[0_0_auto] justify-between items-start gap-1 my-2 pl-2"
    >
        {/*FIRST ATTACHMENT*/}
        {media &&
            <div className="!w-10 !h-10 mr-2 inline-flex justify-center items-center flex-[0_0_auto]">
                <AttachmentIcon media={media} />
            </div>
        }
        {/*MESSAGE*/}
        <div className="w-auto overflow-hidden inline-flex flex-col justify-start items-center">
            {/*AUTHOR*/}
            <div className="w-full text-sm text-melon-blue">В ответ {messageToReply.author_name}</div>
            {/*TEXT OR ATTACHMENT TYPE*/}
            <div className="w-full text-ellipsis text-sm text-melon-grey truncate">
                {messageToReply.text
                    ? messageToReply.text
                    : (media && getMediaTypeName(media.file))}
            </div>
        </div>
        {/*CANCEL REPLY*/}
        {showCancelButton &&
            <div className="flex ml-auto flex-row !w-8 flex-[0_0_auto] h-full justify-end items-center">
                <button
                    className='cursor-pointer bg-melon-light p-1 rounded w-6 h-fit'
                    onClick={onCancelReply}
                >
                    <img src={Cross} width={16} height={16} alt='Close' />
                </button>
            </div>
        }
    </div>
}

export const ChatMessageItem = ({ msg, sender, getFile }: Props) => {
    const { userUuid } = useUserContext();
    const { chatUsers } = useChatContext();


    // Запрос на чтение
    const { mutate: read, isPending } = useMutation({
        mutationFn: ({ pk }: {
            pk: string
        }) => readMessage(pk),
    });

    // Наблюдение за видимостью сообщения если видно и не прочитано шлём read
    // надёжно один раз на сообщение через ref флаг чтобы не терять при быстрой прокрутке
    const messageRef = useRef<HTMLDivElement | null>(null);
    const readSentRef = useRef(false);
    useEffect(() => {
        readSentRef.current = false;
    }, [msg.pk]);
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (
                    entry.isIntersecting
                    && !readSentRef.current
                    && !(msg.seen_by || []).includes(userUuid || '')
                ) {
                    readSentRef.current = true;
                    read({ pk: msg.pk });
                }
            });
        });

        if (messageRef.current) {
            observer.observe(messageRef.current);
        }

        // Отключаем наблюдение при удалении компонента
        return () => {
            observer.disconnect();
        };
    }, [msg, userUuid]);

    const renderMessageContent = (content: string) => {
        // Заменяем непрерываемые пробелы обычными
        let textContent = content.replace(/&nbsp;/g, ' ');
        textContent = textContent.replace(/(\r)+/g, '\n');
        // Заменяем новые строки от 3 штук на одну
        textContent = textContent.replace(/\n{4,}/g, '\n\n');
        // Пробелы по краям удаляем
        textContent = textContent.trim();

        const parser = new DOMParser();
        const doc = parser.parseFromString(textContent, 'text/html');
        const body = doc.body;

        // Regular expression to match URLs
        const urlRegex = /\bhttps?:\/\/[^\s/$.?#].[^\s]*/gi;

        const renderNode = (node: ChildNode): React.ReactNode => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent !== '\n\n') {
                // If the node is a text node and not just two new line characters, render it
                let text = node.textContent || '';
                const parts = text.split(urlRegex);
                const matches = text.match(urlRegex) || [];

                return (
                    <>
                        {parts.map((part, index) => (
                            <React.Fragment key={index}>
                                {part}
                                {index < matches.length && (
                                    <a href={matches[index]} target="_blank" rel="noopener noreferrer">
                                        {matches[index]}
                                    </a>
                                )}
                            </React.Fragment>
                        ))}
                    </>
                );
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                if (['H1', 'H2', 'H3', 'H4'].includes(element.tagName)) {
                    // Заголовки делаем жирным и добавляем тег перехода на новую строку
                    return <><b>{Array.from(element.childNodes).map(renderNode)}</b><br /></>;
                } else if (['B', 'STRONG'].includes(element.tagName)) {
                    // Жирный
                    return <b>{Array.from(element.childNodes).map(renderNode)}</b>;
                } else if (element.tagName === 'I') {
                    // Курсив
                    return <i>{Array.from(element.childNodes).map(renderNode)}</i>;
                } else if (element.tagName === 'S') {
                    // Зачёркнутый
                    return <s>{Array.from(element.childNodes).map(renderNode)}</s>;
                } else if (element.tagName === 'P') {
                    // Параграфам добавляем тег перехода на новую строку
                    return (<>{Array.from(element.childNodes).map(renderNode)}<br /></>);
                } else if (element.tagName === 'BR') {
                    // Переход на новую строку сохраняем
                    return <br />;
                } else {
                    // Рекурсия
                    return Array.from(element.childNodes).map(renderNode);
                }
            }
            return null;
        };

        return Array.from(body.childNodes).map(renderNode);
    };

    const renderReadStatus = (status: MessageStatus | null) => {
        if (status === 'SENT') {
            return <img src={DeliveredIcon} alt='Message sent' />
        }
        if (status === 'READ') {
            return <img src={ReadIcon} alt='Message read' />
        }
        if (status === 'ERROR') {
            return <img src={ErrorIcon} alt='Message error' />
        }
        return null
    }

    // Статус прочтения своего сообщения
    const [readStatus, setReadStatus] = useState<MessageStatus>("SENT")

    // Подготовка сообщения, на которое ответили
    const prepareRepliedMessage = (message: ReplyToMessage) => {
        const foundAuthor = chatUsers.find(e => e.pk === message.author);
        const prepared: ReplyToMessage = {
            pk: message.pk,
            author: message.author,
            author_name: foundAuthor ? `${foundAuthor.first_name} ${foundAuthor.last_name}` : 'удалённый участник',
            text: message.text,
            msg_type: message.msg_type,
            important: message.important,
            media: message.media,
        }
        return prepared;
    }

    // переход к исходному сообщению по клику на превью ответа
    const scrollToMessage = (pk?: string) => {
        if (!pk) return;
        const el = document.getElementById(`chat-msg-${pk}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // короткая подсветка чтобы было видно куда перешли
        el.style.transition = 'background-color 0.4s';
        el.style.backgroundColor = '#2f6fed20';
        setTimeout(() => { el.style.backgroundColor = ''; }, 1200);
    }

    useEffect(() => {
        // console.log(`${msg.text}|${msg.pk}|[${msg.seen_by}]`)
        if ((msg.seen_by || []).filter(e => e !== userUuid).length) {
            setReadStatus('READ')
        }
    }, [msg]);

    return (
        <div
            ref={messageRef}
            id={`chat-msg-${msg.pk}`}
            className={'w-full max-w-full flex flex-row justify-start gap-2 p-5 flex-[0_0_auto] overflow-x-hidden ' + (msg.important ? ' bg-[#f5404010]' : '')}
        >
            {/*Сообщение с автором*/}
            {/*SENDER IMAGE*/}
            {msg.author &&
                <div className={'!h-12 !w-12 flex-[0_0_auto] mt-auto'}>
                    <img
                        className='rounded-full w-full h-full object-cover'
                        src={sender?.photo_img?.file ?? Temp}
                        alt='profile'
                    />
                </div>
            }
            {/*USER MESSAGE*/}
            {msg.author &&
                <div className='flex flex-col truncate'>
                    {/*SENDER NAME*/}
                    <div className='text-melon-yellow'>{sender?.first_name} {sender?.last_name}</div>
                    {/*MESSAGE CONTAINER*/}
                    <div className='flex flex-col'>
                        {msg.replied_to &&
                            <div
                                className='w-full h-full max-h-10 mt-2 flex justify-center items-center cursor-pointer'
                                onClick={() => scrollToMessage(msg.replied_to?.pk)}
                            >
                                <ChatReplyToMessage
                                    messageToReply={prepareRepliedMessage(msg.replied_to)}
                                    onCancelReply={() => {
                                    }}
                                    showCancelButton={false}
                                />
                            </div>
                        }

                        {/*FILES*/}
                        {msg.media && !!msg.media.length &&
                            <div className="
                                rounded-md
                                inline-flex
                                flex-row
                                flex-wrap
                                justify-start
                                items-start
                                gap-3
                                ">
                                {
                                    msg.media.map((media: Media) => {
                                        return <Attachment
                                            key={media.id}
                                            media={media}
                                            getFile={() => getFile ? getFile(media.id) : undefined} />
                                    })
                                }
                            </div>
                        }
                        {/*MESSAGE TEXT*/}
                        <div className="
                            w-full
                            flex-1 overflow-hidden
                            whitespace-pre-wrap
                            break-word-only-long
                            ">
                            {renderMessageContent(msg.text)}
                        </div>
                    </div>
                </div>
            }
            {!msg.author && !msg.author && msg.msg_type !== MessageTypeEnum.TRAVEL_MANAGER_REQUESTED_TRIP_CONFIRMATION &&
                <div className='flex italic font-bold w-full'>
                    <div>{msg.text}</div>
                </div>
            }

            {/*Время и статус*/}
            <div className='inline-flex text-melon-grey-darker mt-auto ml-auto w-20 flex-row justify-end items-center'>
                {/*Статус прочтения моего сообщения*/}
                <div className={'w-6'}>
                    {msg.author === userUuid && renderReadStatus(readStatus)}
                </div>

                {/*Дата время*/}
                <div className={'w-14'}>
                    {new Date(msg.created_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    })}
                </div>
            </div>
        </div>
    );
};
